import { Button, HexIcon, Icons, Typography, Stack } from '@cast/design-system';

import { FeedbackDialog } from 'components/dialogs';
import { useDeleteEdgeLocationMutation } from 'hooks/mutations/omni';

import { EnhancedEdgeLocation } from '../../types';

export type DeleteEdgeLocationDialogProps = {
  edgeLocation: EnhancedEdgeLocation;
  clusterId: string;
};

type Props = DeleteEdgeLocationDialogProps & {
  onClose: () => void;
};

export const DeleteEdgeLocationDialog = ({
  edgeLocation,
  clusterId,
  onClose,
}: Props) => {
  const { mutate: deleteEdgeLocation, isPending } =
    useDeleteEdgeLocationMutation({ clusterId, onSuccess: onClose });

  const handleDelete = () => {
    deleteEdgeLocation(edgeLocation.id!);
  };

  return (
    <FeedbackDialog
      testId="delete-edge-location-dialog"
      icon={<HexIcon variant="error" icon={<Icons.Warning weight="fill" />} />}
      title="Are you sure you want to delete this edge location?"
      contentSx={{ maxWidth: 496, margin: 'auto' }}
      bodySx={{ maxWidth: 366 }}
      body={
        <Stack gap={8}>
          <Typography variant="h6" color="grey.900">
            {edgeLocation.name}
          </Typography>
          <Typography variant="P12R" color="textSecondary">
            This will delete both the edge location and its associated edge node
            configuration. This action cannot be undone.
          </Typography>
        </Stack>
      }
      destroy={onClose}
      actions={
        <Stack direction="row" gap={16} mb={32} justifyContent="center">
          <Button testId="cancel-button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            testId="delete-button"
            variant="danger"
            onClick={handleDelete}
            loading={isPending}
          >
            Delete
          </Button>
        </Stack>
      }
    />
  );
};
